import mime from 'mime/lite';

const URL_REGEX = /https?:\/\/[^\s]+/gi;

const isPdfReference = (value: string): boolean => {
  let pathOnly = value.split('?')[0];
  try {
    pathOnly = decodeURIComponent(pathOnly);
  } catch {
    // Keep malformed URLs as plain text instead of failing during render.
    return false;
  }
  const mimeType = mime.getType(pathOnly);
  return mimeType === 'application/pdf' || pathOnly.toLowerCase().endsWith('.pdf');
};

export interface ParsedFeedback {
  displayText: string | null;
  pdfSource: string | null;
}

export interface ParseFeedbackOptions {
  /** Dedicated PDF field (e.g. UGC `editRequestPdf`); wins over PDFs embedded in text. */
  pdfUrl?: string | null;
}

const looksLikeFileReference = (value: string): boolean =>
  /^https?:\/\//i.test(value) || (!/\s/.test(value) && value.includes('/'));

const parseFeedbackFromText = (feedback: string | null | undefined): ParsedFeedback => {
  if (!feedback?.trim()) {
    return { displayText: null, pdfSource: null };
  }

  const trimmed = feedback.trim();

  const embeddedUrls = trimmed.match(URL_REGEX) ?? [];
  const pdfUrl = embeddedUrls.find((url) => isPdfReference(url.replace(/[),.]+$/, '')));
  if (pdfUrl) {
    const cleanedUrl = pdfUrl.replace(/[),.]+$/, '');
    const text = trimmed.replace(pdfUrl, '').replace(/\s+/g, ' ').trim();
    return { displayText: text || null, pdfSource: cleanedUrl };
  }

  if (looksLikeFileReference(trimmed) && isPdfReference(trimmed)) {
    return { displayText: null, pdfSource: trimmed };
  }

  return { displayText: trimmed, pdfSource: null };
};

/**
 * Splits brand feedback into readable text and an optional PDF reference
 * (full URL or Firebase Storage path).
 *
 * Prefer a dedicated PDF field when present (UGC `editRequestPdf`); otherwise
 * fall back to a PDF URL/path embedded in the feedback string.
 */
export const parseFeedback = (
  feedback: string | null | undefined,
  options?: ParseFeedbackOptions
): ParsedFeedback => {
  const fromText = parseFeedbackFromText(feedback);
  const dedicated = options?.pdfUrl?.trim() || null;
  if (dedicated) {
    return {
      displayText: fromText.displayText,
      pdfSource: dedicated,
    };
  }
  return fromText;
};
